import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import LoadingLabel from "./LoadingLabel";
import { lockBodyScroll } from "../lib/scroll-lock";
export type ConfirmState = {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
};
export default function ConfirmDialog({
  state,
  onClose,
}: {
  state: ConfirmState | null;
  onClose: () => void;
}) {
  const ref = useRef<HTMLElement>(null),
    close = useRef(onClose);
  close.current = onClose;
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const busyRef = useRef(false);
  useEffect(() => {
    if (!state) return;
    setError("");
    const previous = document.activeElement as HTMLElement;
    const unlock = lockBodyScroll();
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!busyRef.current) close.current();
      }
      if (e.key === "Tab") {
        const nodes = Array.from(
          ref.current?.querySelectorAll<HTMLButtonElement>(
            "button:not(:disabled)",
          ) || [],
        );
        if (!nodes.length) {
          e.preventDefault();
          return;
        }
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (
          !ref.current?.contains(document.activeElement) ||
          (e.shiftKey && document.activeElement === first)
        ) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("keydown", key, true);
      unlock();
      previous?.focus();
    };
  }, [state]);
  if (!state) return null;
  return (
    <div className="confirm-backdrop" onMouseDown={() => !busy && onClose()}>
      <section
        ref={ref}
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-busy={busy}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          className="confirm-close"
          aria-label="Cancel"
          disabled={busy}
          onClick={onClose}
        >
          <X size={18} />
        </button>
        <h3 id="confirm-title">{state.title}</h3>
        <p>{state.message}</p>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <div className="confirm-actions">
          <button disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            disabled={busy}
            className={state.danger ? "danger-button" : ""}
            onClick={async () => {
              if (busyRef.current) return;
              busyRef.current = true;
              setBusy(true);
              setError("");
              try {
                await state.onConfirm();
                onClose();
              } catch (e) {
                setError(
                  e instanceof Error
                    ? e.message
                    : "Could not finish. Please try again.",
                );
              } finally {
                busyRef.current = false;
                setBusy(false);
              }
            }}
          >
            {busy ? <LoadingLabel>Working</LoadingLabel> : state.confirmLabel || "Confirm"}
          </button>
        </div>
      </section>
    </div>
  );
}
